import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { FormGroup } from '@/components/element/Form/FormGroup';
import { InputPrice } from '@/components/element/Form/InputPrice';
import { SelectOptionType } from '@/components/element/Form/SelectOptionType';
import { SelectSingle } from '@/components/element/Form/SelectSingle';
import { ScrollTop } from '@/components/element/ScrollTop';
import { BusinessPartnerCreateForm } from '@/components/features/BusinessPartnerCreateForm';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useProducts } from '@/hooks/api/0390_products';
import { useBusinessPartners, useInvalidateBusinessPartners } from '@/hooks/api/0430_business_partners';
import { useBillingTargets } from '@/hooks/api/1200_billing_targets';
import { useSalesOrdersInvalidate } from '@/hooks/api/1210_sales_orders';
import { useCreateSalesOrder } from '@/hooks/api/1220_create_sales_order';
import { BillingTarget, BusinessPartner, Product, SalesOrder } from '@/hooks/api/models';
import { ProjectLogic } from '@/hooks/api/models/logics';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { scrollTop } from '@/utils';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import { useEffect, useRef, useState } from 'react';
import Flatpickr from 'react-flatpickr';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';

type FormInputs = {
  identifier: string;
  team_id: number;
  name: string;
  description: string;
  user_id: number;
  business_partner_id: number;
  agency_id?: number;
  person_in_charge?: string;
  billing_target_id: number;
  product_id: number;
  spot_amount?: number;
  spot_billing_date?: string;
  monthly_amount?: number;
  monthly_billing_start_date?: string;
  monthly_billing_end_date?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  delivery_date?: string;
  task_procedure_manual?: string;
};

type Props = { businessPartners: BusinessPartner[]; billingTargets: BillingTarget[]; products: Product[] };

export const SalesOrdersNew = () => {
  const { _w_ } = useCurrentTeamMember();
  const { data: businessPartnersData } = useBusinessPartners({
    identifier: _w_!.identifier,
    display_type: 'sales',
    page: 1,
    per: 1000,
  });
  const { data: billingTargets } = useBillingTargets();
  const { data: products } = useProducts({ identifier: _w_!.identifier });
  const [businessPartners, setBusinessPartners] = useState<BusinessPartner[] | null>(null);
  useEffect(() => {
    if (businessPartnersData) {
      setBusinessPartners(() => businessPartnersData.business_partners);
    }
  }, [businessPartnersData]);

  if (businessPartners == null || billingTargets == null || products == null) return <></>;

  return <_SalesOrdersNew businessPartners={businessPartners} billingTargets={billingTargets} products={products} />;
};

export const _SalesOrdersNew = ({ businessPartners, billingTargets, products }: Props) => {
  const { setTitle, setBreadcrumbs } = useNavState();
  const { _w_, _t_, me } = useCurrentTeamMember();
  useEffect(() => setTitle(`新規プロジェクト（売上）作成`), []);
  useEffect(
    () =>
      setBreadcrumbs([
        { label: `Sales Orders - ${_t_!.name}`, path: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders` },
      ]),
    []
  );

  const location = useLocation();
  const copyFrom: SalesOrder = location.state?.copyFrom; // コピーして作成の場合はSalesOrderが渡される
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { mutate, data, isPending, error } = useCreateSalesOrder();
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const { invalidate } = useSalesOrdersInvalidate(_w_!.identifier, _t_!.id);
  const [businessPartnerOptions, setBusinessPartnerOptions] = useState<SelectOptionType[]>();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    trigger,
    resetField,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
    },
  });
  const [productId, monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate] = watch([
    'product_id',
    'monthly_amount',
    'monthly_billing_start_date',
    'monthly_billing_end_date',
  ]);

  useEffect(() => {
    if (businessPartners) {
      setBusinessPartnerOptions(
        businessPartners.map((d): SelectOptionType => ({ value: d.id, label: d.display_name }))
      );
    }

    if (businessPartners && billingTargets && products) {
      if (copyFrom) {
        // コピーから作成
        setValue('name', `コピー - ${copyFrom.name}`);
        setValue('description', copyFrom.description || '');
        setValue('user_id', copyFrom.user.id);
        setValue('business_partner_id', copyFrom.business_partner.id);
        setValue('person_in_charge', copyFrom.person_in_charge);
        setValue('product_id', copyFrom.product.id);
        setSelectedProduct((_) => products.find((p) => p.id == copyFrom.product.id));
        if (_t_!.agency_enabled) {
          setValue('agency_id', copyFrom.agency?.id);
          setValue('billing_target_id', copyFrom.billing_target.id);
        } else {
          setValue('billing_target_id', billingTargets[0].id);
        }
        if (copyFrom.product.spot_available) {
          setValue('spot_amount', copyFrom.spot_amount);
          setValue('spot_billing_date', copyFrom.spot_billing_date);
        }
        if (copyFrom.product.monthly_available) {
          setValue('monthly_amount', copyFrom.monthly_amount);
          setValue('monthly_billing_start_date', copyFrom.monthly_billing_start_date);
          setValue('monthly_billing_end_date', copyFrom.monthly_billing_end_date);
        }
        if (copyFrom.product.contract_term_available) {
          setValue('contract_start_date', copyFrom.contract_start_date);
          setValue('contract_end_date', copyFrom.contract_end_date);
        }
        if (copyFrom.product.delivery_date_available) {
          setValue('delivery_date', copyFrom.delivery_date);
        }
        setValue('task_procedure_manual', copyFrom.task_procedure_manual);
      } else {
        setValue('user_id', me!.id);
        setValue('billing_target_id', billingTargets[0].id);
      }
    }
  }, [businessPartners, billingTargets, products, copyFrom]);

  useEffect(() => {
    if (productId) {
      const _selectedProduct = products.find((p) => p.id == productId);

      if (_selectedProduct) {
        if (selectedProduct?.id != productId) {
          setSelectedProduct((_) => _selectedProduct);
          if (_selectedProduct.spot_available) {
            setValue('spot_amount', _selectedProduct.spot_amount ? _selectedProduct.spot_amount : 0);
          }

          if (_selectedProduct.monthly_available) {
            setValue('monthly_amount', _selectedProduct.monthly_amount);
          }
        }
      }
    }
  }, [productId]);

  useEffect(() => {
    if (monthlyAmount && monthlyBillingStartDate && monthlyBillingEndDate) {
      setTotalAmount((_) =>
        ProjectLogic.calcTotalAmount(monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate)
      );
    } else {
      setTotalAmount((_) => 0);
    }
  }, [monthlyAmount, monthlyBillingStartDate, monthlyBillingEndDate]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('プロジェクト（売上）の登録が完了しました');
      navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${data.id}/billing_schedules`);
    }
  }, [data]);

  useEffect(() => {
    if (error) scrollTop();
  }, [error]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  const onBusinessPartnerChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      setValue('business_partner_id', sv.value);
      trigger('business_partner_id');
    } else {
      resetField('business_partner_id');
    }
  };

  const onAgencyChange = (sv: SingleValue<SelectOptionType>) => {
    if (sv) {
      // 代理店を選択した際にPayerを代理店に変更
      setValue('billing_target_id', billingTargets[1].id);
      setValue('agency_id', sv.value);
      trigger('agency_id');
    } else {
      resetField('agency_id');
    }
  };

  const submitBusinessPartnerButtonRef = useRef<HTMLButtonElement>(null);
  const businessPartnerModalCloseButtonRef = useRef<HTMLButtonElement>(null);
  const { invalidate: invalidateBusinessPartners } = useInvalidateBusinessPartners(_w_!.identifier);
  const onBusinessPartnerCreated = (businessPartner: BusinessPartner) => {
    businessPartnerModalCloseButtonRef.current?.click();
    invalidateBusinessPartners();
  };

  return (
    <WorkspaceLayout>
      <ScrollTop />

      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12 col-lg-9 mx-auto">
            <div className="card mb-4" id="basic-info">
              <div className="card-header d-flex align-items-center">
                <div className="col-md-8">
                  <h5 className="mb-0">新規プロジェクト（売上）作成</h5>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="card-body pt-0 row">
                  <ApiErrorMessages error={error} />

                  <div className="mb-3">
                    <div className="mb-0 font-weight-bold">プロジェクト基本情報</div>
                  </div>
                  <FormGroup label="プロジェクト名" required={true}>
                    <input
                      type="text"
                      maxLength={50}
                      className={`form-control ${errors.name && 'is-invalid'}`}
                      placeholder={'案件名'}
                      {...register('name', {
                        required: '入力して下さい。',
                      })}
                    />
                    {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                  </FormGroup>

                  <FormGroup label="メモ">
                    <textarea
                      className="form-control"
                      maxLength={2000}
                      rows={5}
                      {...register('description')}
                    ></textarea>
                  </FormGroup>

                  <div className="row">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="プロジェクト担当者" required={true}>
                        <select className="form-control" required={true} {...register('user_id')}>
                          {_t_!.team_members.map((tm, i) => (
                            <option key={`tm-user-${tm.id}`} value={tm.user.id}>
                              {tm.user.user_account.name}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="取引先" required={true}>
                      <div className="d-flex align-items-center">
                        <Controller
                          control={control}
                          name="business_partner_id"
                          rules={{ required: true }}
                          render={({ field: { ref, ...field } }) => (
                            <SelectSingle
                              options={businessPartnerOptions}
                              selectedId={getValues('business_partner_id')}
                              className={'w-100'}
                              onChange={onBusinessPartnerChange}
                              error={errors.business_partner_id != null}
                            />
                          )}
                        />
                        <button
                          className="btn btn-icon-only bg-gradient-secondary ms-1"
                          data-bs-toggle="modal"
                          data-bs-target="#modal-add-business-partner"
                          type="button"
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </FormGroup>
                  </div>

                  <div className="col-12 col-md-6">
                    <FormGroup label="取引先担当者">
                      <input type="text" className="form-control" {...register('person_in_charge')} />
                    </FormGroup>
                  </div>

                  {_t_!.agency_enabled && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="代理店">
                          <div className="d-flex align-items-center">
                            <Controller
                              control={control}
                              name="agency_id"
                              rules={{}}
                              render={({ field: { ref, ...field } }) => (
                                <SelectSingle
                                  options={businessPartnerOptions}
                                  selectedId={getValues('agency_id')}
                                  className={'w-100'}
                                  onChange={onAgencyChange}
                                />
                              )}
                            />
                            <button
                              className="btn btn-icon-only bg-gradient-secondary ms-1"
                              data-bs-toggle="modal"
                              data-bs-target="#modal-add-business-partner"
                              type="button"
                            >
                              <i className="fas fa-plus"></i>
                            </button>
                          </div>
                        </FormGroup>
                      </div>
                      <div className="col-12 col-md-6">
                        <FormGroup label="請求先" required={true}>
                          <select className="form-control" required={true} {...register('billing_target_id')}>
                            {billingTargets!.map((bt, i) => (
                              <option key={`bt-${bt.id}`} value={bt.id}>
                                {bt.label}
                              </option>
                            ))}
                          </select>
                        </FormGroup>
                      </div>
                    </>
                  )}
                  <div className="my-3">
                    <div className="mb-1 font-weight-bold">プロジェクト詳細情報</div>
                    <p className="text-xs mb-0">契約タスク承認後は承認者のみが金額や期間の変更を行えます</p>
                  </div>
                  <div className="row">
                    <div className="col-12 col-md-6 pe-0">
                      <FormGroup label="売上カテゴリ" required={true}>
                        <select className="form-control" required={true} {...register('product_id')}>
                          <option value=""></option>
                          {products!.map((p, i) => (
                            <option key={`product-${p.id}`} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </FormGroup>
                    </div>
                  </div>

                  {selectedProduct?.spot_available && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="一括請求金額（税抜）" required={true}>
                          <div className="input-group">
                            <Controller
                              render={({ field: { onChange, value, ...field } }) => (
                                <InputPrice onChange={onChange} value={value} error={errors.spot_amount} />
                              )}
                              control={control}
                              name="spot_amount"
                              rules={{ required: true }}
                            />
                          </div>
                          <div className="text-xs text-muted mt-1">納品時等一括で請求する金額</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="請求日" required={true}>
                          <Controller
                            control={control}
                            name="spot_billing_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${errors.spot_billing_date && 'is-invalid'}`}
                                {...field}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, spotBillingDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={(e) => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                        </FormGroup>
                      </div>
                    </>
                  )}

                  {selectedProduct?.monthly_available && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="月額請求金額（税抜）" required={true}>
                          <div className="input-group">
                            <div className="input-group">
                              <Controller
                                render={({ field: { onChange, value, ...field } }) => (
                                  <InputPrice onChange={onChange} value={value} error={errors.monthly_amount} />
                                )}
                                control={control}
                                name="monthly_amount"
                                rules={{ required: true }}
                              />
                            </div>
                          </div>
                          <div className="text-xs text-muted mt-1">&nbsp;</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="月額合計金額">
                          <input
                            type="text"
                            className="form-control text-end"
                            value={`¥${totalAmount.toLocaleString()}`}
                            disabled
                          />
                          <div className="text-xs text-muted mt-1">自動計算されます</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="請求対象期間の開始日" required={true}>
                          <Controller
                            control={control}
                            name="monthly_billing_start_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${
                                  errors.monthly_billing_start_date && 'is-invalid'
                                }`}
                                {...field}
                                onChange={(_, dateString) => {
                                  // monthly_billing_start_dateと契約開始日が同じなら上書き
                                  const curMonthlyBillingStartDate = getValues('monthly_billing_start_date');
                                  const curContractStartDate = getValues('contract_start_date');
                                  if (
                                    curContractStartDate == null ||
                                    curMonthlyBillingStartDate == curContractStartDate
                                  ) {
                                    setValue('contract_start_date', dateString);
                                  }

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">月額金額の請求対象となる期間の開始日</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="請求対象期間の終了日" required={true}>
                          <Controller
                            control={control}
                            name="monthly_billing_end_date"
                            rules={{ required: true }}
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr ${errors.monthly_billing_end_date && 'is-invalid'}`}
                                {...field}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, monthlyBillingEndDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">月額金額の請求対象となる期間の終了日</div>
                        </FormGroup>
                      </div>
                    </>
                  )}

                  {selectedProduct && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="契約開始日">
                          <Controller
                            control={control}
                            name="contract_start_date"
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr`}
                                {...field}
                                onChange={(_, dateString) => {
                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">契約書・発注書等に記載の契約開始となる日付</div>
                        </FormGroup>
                      </div>

                      <div className="col-12 col-md-6">
                        <FormGroup label="契約終了日">
                          <Controller
                            control={control}
                            name="contract_end_date"
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr`}
                                {...field}
                                onChange={(_, dateString) => {
                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">契約書・発注書等に記載の契約終了となる日付</div>
                        </FormGroup>
                      </div>
                    </>
                  )}

                  {selectedProduct?.delivery_date_available && (
                    <>
                      <div className="col-12 col-md-6">
                        <FormGroup label="納品日">
                          <Controller
                            control={control}
                            name="delivery_date"
                            render={({ field: { ref, ...field } }) => (
                              <Flatpickr
                                className={`form-control datepickr`}
                                {...field}
                                onChange={(_, dateString) => {
                                  const cur = {
                                    contractEndDate: getValues('contract_end_date') || '',
                                    spotBillingDate: getValues('spot_billing_date') || '',
                                    monthlyBillingEndDate: getValues('monthly_billing_end_date') || '',
                                    deliveryDate: getValues('delivery_date') || '',
                                  };
                                  const dst = { ...cur, deliveryDate: dateString };
                                  const contractEndDate = ProjectLogic.detectContractEndDate({ cur, dst });
                                  setValue('contract_end_date', contractEndDate);

                                  field.onChange(dateString);
                                  field.onBlur();
                                }}
                                onBlur={() => {}}
                                options={{ locale: Japanese, dateFormat: 'Y/m/d' }}
                              />
                            )}
                          />
                          <div className="text-xs text-muted mt-1">契約書・発注書等に記載の納品の日付</div>
                        </FormGroup>
                      </div>
                    </>
                  )}

                  <FormGroup label="請求タスク処理マニュアル">
                    <textarea
                      className="form-control"
                      maxLength={2000}
                      rows={5}
                      {...register('task_procedure_manual')}
                    ></textarea>
                    <div className="text-xs text-muted mt-1">請求タスクに連携される事務処理手順</div>
                  </FormGroup>
                </div>
                <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                  <Link to="" className="btn btn-outline-secondary me-2" onClick={() => navigate(-1)}>
                    戻る
                  </Link>
                  <button className="btn bg-gradient-dark btn-icon">
                    <i className="fas fa-check-circle"></i>保存して請求予定詳細へ
                  </button>
                </div>
              </form>

              {/* 新規Client登録用モーダル */}
              <div
                className="modal fade"
                id="modal-add-business-partner"
                role="dialog"
                aria-labelledby="modal-form"
                aria-hidden="true"
              >
                <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                  <div className="modal-content">
                    <div className="modal-content">
                      <div className="modal-header border-0 pb-0">
                        <h5 className="modal-title">新規取引先登録</h5>
                        <button
                          type="button"
                          className="btn-close text-dark"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        >
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </div>
                      <div className="modal-header pt-2">
                        <p className="text-xs mb-0">
                          取引先の情報を入力してください。
                          <br />
                          登録したい取引先の法人番号を入力すると企業名等が自動設定されます。
                          <br />
                          法人番号は
                          <Link
                            to="https://www.houjin-bangou.nta.go.jp/"
                            className="text-decoration-underline"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            法人番号公表サイト
                          </Link>
                          で検索することができます。
                        </p>
                        <p className="text-xs mb-0"></p>
                      </div>
                      <div className="modal-body">
                        <BusinessPartnerCreateForm
                          onCreated={onBusinessPartnerCreated}
                          submitButtonRef={submitBusinessPartnerButtonRef}
                          displayType={'sales'}
                        />
                      </div>
                      <div className="modal-footer">
                        <button
                          type="button"
                          className="btn btn-outline-secondary modal-close-btn"
                          data-bs-dismiss="modal"
                          ref={businessPartnerModalCloseButtonRef}
                        >
                          Close
                        </button>
                        <button
                          type="button"
                          className="btn bg-gradient-dark btn-icon"
                          onClick={() => submitBusinessPartnerButtonRef.current?.click()}
                        >
                          <i className="fas fa-check-circle"></i>新規登録する
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};
